import { MetricUnit } from '@/__generated__/graphql';
import { useFormatters } from '@/hooks/useFormatters';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { FC, useId } from 'react';

interface ProgressBarProps {
  value?: number;
  label?: string;
  timeText?: string;
  speed?: number;
  color: string;
  indeterminate?: boolean;
}

export const ProgressBar: FC<ProgressBarProps> = ({
  label,
  timeText,
  speed = 3,
  color,
  indeterminate,
  value = 0,
}) => {
  const { formatMetricValue } = useFormatters();
  const uniqueId = useId();
  const patternId = `pattern-${uniqueId}`;

  const height = 26;
  const patternWidth = 14;
  const barWidth = 10;
  const skew = -20;
  const mainColor = color;

  const getPatternAnimProps = () => {
    const base = {
      attributeName: 'x',
      from: '0',
      to: patternWidth.toString(),
      dur: `${8 / speed}s`,
      repeatCount: 'indefinite',
    };
    return { ...base, calcMode: 'linear' };
  };

  return (
    <Box sx={{ px: 2, width: '100%', color: 'white' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
        <Typography fontWeight="medium" sx={{ fontSize: 14 }}>
          {label}
        </Typography>
        {!indeterminate && (
          <Typography variant="body2" sx={{ fontSize: 14, fontWeight: 'bold' }}>
            {formatMetricValue(value, { unit: MetricUnit.Percent, precision: 0 })}
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          border: `2px solid ${mainColor}4D`,
          borderRadius: 1,
          height: '34px',
          p: '3px',
          boxShadow: `0 0 12px -4px ${mainColor}40`,
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
        }}
      >
        <svg
          width="100%"
          height="100%"
          style={{
            borderRadius: 1,
            display: 'block',
          }}
        >
          <defs>
            <pattern
              id={patternId}
              x="0"
              y="0"
              width={patternWidth}
              height={height}
              patternUnits="userSpaceOnUse"
              patternTransform={`skewX(${skew})`}
            >
              <rect x="0" y="0" width={barWidth} height={height} fill={mainColor} opacity="0.9" />
              <animate {...getPatternAnimProps()} />
            </pattern>
          </defs>

          {indeterminate ? (
            <rect y="0" height="100%" rx="4" fill={mainColor} opacity="0.6">
              <animate
                attributeName="x"
                values="0%; 90%; 0%"
                keyTimes="0; 0.35; 1"
                calcMode="spline"
                keySplines="0.4 0 0.2 1; 0.4 0 0.2 1"
                dur="3s"
                repeatCount="indefinite"
              />

              <animate
                attributeName="width"
                values="10%; 20%; 10%; 20%; 10%"
                keyTimes="0; 0.17; 0.35; 0.675; 1"
                calcMode="spline"
                keySplines="0.4 0 0.2 1; 0.4 0 0.2 1; 0.4 0 0.2 1; 0.4 0 0.2 1"
                dur="3s"
                repeatCount="indefinite"
              />
            </rect>
          ) : (
            <rect
              x="0"
              y="0"
              width={`${value}%`}
              height="100%"
              fill={`url(#${patternId})`}
              style={{ transition: 'width 0.8s cubic-bezier(0.4, 0, 0.2, 1)' }}
            />
          )}
        </svg>
      </Box>

      {/* Footer */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
        <Typography sx={{ fontSize: '0.85rem', opacity: 1 }}>{timeText}</Typography>
      </Box>
    </Box>
  );
};
